// frontend/src/pages/OAuthCallback.jsx
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { routeForGateStep } from '../utils/accessGate';

const OAuthCallback = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const token     = searchParams.get('token');
        const needsSetup = searchParams.get('needsSetup');
        const error     = searchParams.get('error');
        const provider  = searchParams.get('provider') || 'google'; // 'google' | 'linkedin'

        if (error) {
            const msgs = {
                google_auth_failed:   'Google authentication failed. Please try again.',
                linkedin_auth_failed: 'LinkedIn authentication failed. Please try again.',
                callback_failed:      'Authentication callback failed. Please try again.',
                linkedin_unavailable: 'LinkedIn sign-in is not available yet. Please use Google or email.',
            };
            toast.error(msgs[error] || 'Authentication error. Please try again.');
            navigate('/login');
            return;
        }

        if (!token) {
            toast.error('No authentication token received');
            navigate('/login');
            return;
        }

        // Always persist the token first
        localStorage.setItem('gigship_token', token);
        // Set default header so subsequent api calls carry the token
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // AuthContext reads the token only on page load, so an in-app navigate() would hit
        // ProtectedRoute as logged-out and bounce to /login. Use a full page load instead.
        if (needsSetup === 'true') {
            // New social user (Google or LinkedIn) — send to the unified onboarding flow.
            // ?social=true is a generic signal; Onboarding uses it to surface SocialSignupSetup.
            window.location.replace(`/onboarding?social=true&provider=${provider}`);
            return;
        }

        // Returning social user — same dashboard access gate as email login
        const checkProgress = async () => {
            try {
                const res = await api.get('/auth/onboarding-gate');
                const gatePath = routeForGateStep(res.data?.step);
                if (gatePath) {
                    // ?send=1 asks the verify page to send fresh codes (router state is lost on a full reload)
                    window.location.replace(res.data.step === 'verify-contact' ? `${gatePath}?send=1` : gatePath);
                    return;
                }
            } catch (_) {
                // API not available — fall through; the dashboard gate re-checks on arrival
            }

            toast.success('Logged in successfully!');
            let destination = '/dashboard';
            try {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                if (decoded?.activeRole === 'shipper') destination = '/gigs';
                else if (decoded?.activeRole === 'gigger') destination = '/requests';
            } catch (_) { }
            window.location.href = destination;
        };

        checkProgress();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Completing your login…</p>
            </div>
        </div>
    );
};

export default OAuthCallback;

